import React, { createContext, useContext, useState, useEffect } from 'react';
import CustomAlert from '../components/CustomAlert';
import { setGlobalAlertFunctions } from '../utils/globalAlertOverride';

const AlertContext = createContext();

export const useGlobalAlert = () => {
  const context = useContext(AlertContext);
  if (!context) {
    throw new Error('useGlobalAlert must be used within AlertProvider');
  }
  return context;
};

export const AlertProvider = ({ children }) => {
  const [alertState, setAlertState] = useState({
    isOpen: false,
    title: '',
    message: '',
    type: 'info',
    onConfirm: null,
    onCancel: null,
    confirmText: 'OK',
    cancelText: 'Cancel',
    showCancel: false
  });

  const showAlert = (message, title = '', type = 'info', confirmText = 'OK') => {
    return new Promise((resolve) => {
      setAlertState({
        isOpen: true,
        title,
        message,
        type,
        confirmText,
        cancelText: 'Cancel',
        showCancel: false,
        onConfirm: () => {
          resolve(true);
        },
        onCancel: () => {
          resolve(false);
        }
      });
    });
  };

  const showConfirm = (message, title = 'Confirm', confirmText = 'Confirm', cancelText = 'Cancel') => {
    return new Promise((resolve) => {
      setAlertState({
        isOpen: true,
        title,
        message,
        type: 'confirm',
        confirmText,
        cancelText,
        showCancel: true,
        onConfirm: () => {
          resolve(true);
        },
        onCancel: () => {
          resolve(false);
        }
      });
    });
  };

  const showSuccess = (message, title = 'Success') => {
    return showAlert(message, title, 'success');
  };

  const showError = (message, title = 'Error') => {
    return showAlert(message, title, 'error');
  };

  const showWarning = (message, title = 'Warning') => {
    return showAlert(message, title, 'warning');
  };

  const closeAlert = () => {
    setAlertState(prev => ({ ...prev, isOpen: false }));
  };

  // Set up global override on mount
  useEffect(() => {
    setGlobalAlertFunctions(showAlert, showConfirm);
  }, []);

  return (
    <AlertContext.Provider
      value={{
        showAlert,
        showConfirm,
        showSuccess,
        showError,
        showWarning,
        closeAlert
      }}
    >
      {children}
      <CustomAlert
        isOpen={alertState.isOpen}
        onClose={closeAlert}
        onConfirm={alertState.onConfirm}
        onCancel={alertState.onCancel}
        title={alertState.title}
        message={alertState.message}
        type={alertState.type}
        confirmText={alertState.confirmText}
        cancelText={alertState.cancelText}
        showCancel={alertState.showCancel}
      />
    </AlertContext.Provider>
  );
};
